import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "../auth/AuthProvider";
import { ErrorAlert } from "../components/ErrorAlert";

import { GalleryCard } from "./gallery/GalleryCard";
import { GallerySchoolPicker } from "./gallery/GallerySchoolPicker";
import { GalleryViewer } from "./gallery/GalleryViewer";

import type { ApiErrorCode } from "../api/client";
import type { GallerySchool } from "./gallery/GallerySchoolPicker";
import type {
  GalleryClassList,
  GalleryPostCard,
  GalleryReactionState,
} from "@canvaschool/contracts";

type Sort = "recent" | "popular";
type Status = "loading" | "ready" | "error";

const SORTS: Array<{ sort: Sort; label: string }> = [
  { sort: "recent", label: "최신순" },
  { sort: "popular", label: "인기순" },
];

/** 학교 밖 갤러리: every school's shared work, reachable by anyone signed in. */
export const GalleryPage = () => {
  const auth = useAuth();
  const { api } = auth;
  const [school, setSchool] = useState<GallerySchool | null>(null);
  const [classes, setClasses] = useState<GalleryClassList["classes"]>([]);
  const [classId, setClassId] = useState<string | null>(null);
  const [sort, setSort] = useState<Sort>("recent");
  const [items, setItems] = useState<GalleryPostCard[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [status, setStatus] = useState<Status>("loading");
  const [error, setError] = useState<ApiErrorCode | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [moreError, setMoreError] = useState<ApiErrorCode | null>(null);
  const [retry, setRetry] = useState(0);
  const [openId, setOpenId] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const moreBusyRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const handleErrorRef = useRef(auth.handleError);
  handleErrorRef.current = auth.handleError;

  const filtered = school !== null;
  const schoolCode = school?.code ?? null;

  // The first page of the current filter. A newer filter aborts this one.
  useEffect(() => {
    const controller = new AbortController();
    controllerRef.current?.abort();
    controllerRef.current = controller;
    moreBusyRef.current = false;
    setMoreBusy(false);
    setMoreError(null);
    setStatus("loading");
    setError(null);
    setItems([]);
    setNextCursor(null);
    api
      .listGalleryPosts(
        {
          sort: schoolCode ? undefined : sort,
          schoolCode: classId ? undefined : schoolCode ?? undefined,
          classId: classId ?? undefined,
        },
        controller.signal,
      )
      .then((page) => {
        if (controller.signal.aborted) {
          return;
        }
        setItems(page.items);
        setNextCursor(page.nextCursor);
        setStatus("ready");
      })
      .catch((failure) => {
        if (!controller.signal.aborted) {
          setError(handleErrorRef.current(failure));
          setStatus("error");
        }
      });
    return () => controller.abort();
  }, [api, schoolCode, classId, sort, retry]);

  // The class chips of the chosen school.
  useEffect(() => {
    setClasses([]);
    if (!schoolCode) {
      return undefined;
    }
    let current = true;
    api
      .listGalleryClasses(schoolCode)
      .then((found) => {
        if (current) {
          setClasses(found);
        }
      })
      .catch(() => undefined);
    return () => {
      current = false;
    };
  }, [api, schoolCode]);

  const loadMore = useCallback(async () => {
    const controller = controllerRef.current;
    if (!controller || !nextCursor || moreBusyRef.current) {
      return;
    }
    moreBusyRef.current = true;
    setMoreBusy(true);
    setMoreError(null);
    try {
      const page = await api.listGalleryPosts(
        {
          sort: schoolCode ? undefined : sort,
          schoolCode: classId ? undefined : schoolCode ?? undefined,
          classId: classId ?? undefined,
          cursor: nextCursor,
        },
        controller.signal,
      );
      if (controller.signal.aborted) {
        return;
      }
      setItems((current) => {
        const known = new Set(current.map((post) => post.id));
        return [
          ...current,
          ...page.items.filter((post) => !known.has(post.id)),
        ];
      });
      setNextCursor(page.nextCursor);
    } catch (failure) {
      if (!controller.signal.aborted) {
        setMoreError(handleErrorRef.current(failure));
      }
    } finally {
      if (!controller.signal.aborted) {
        moreBusyRef.current = false;
        setMoreBusy(false);
      }
    }
  }, [api, nextCursor, schoolCode, classId, sort]);

  const canObserve = typeof IntersectionObserver !== "undefined";
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (
      !canObserve ||
      !sentinel ||
      !nextCursor ||
      moreBusy ||
      moreError ||
      status !== "ready"
    ) {
      return undefined;
    }
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        void loadMore();
      }
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canObserve, nextCursor, moreBusy, moreError, status, loadMore]);

  const patch = (id: string, change: Partial<GalleryPostCard>) =>
    setItems((current) =>
      current.map((post) => (post.id === id ? { ...post, ...change } : post)),
    );
  const applyReaction = (id: string, state: GalleryReactionState) =>
    patch(id, { reactions: state.reactions, myReactions: state.myReactions });

  const openPost = items.find((post) => post.id === openId) ?? null;

  return (
    <section className="canvaschool-page canvaschool-gallery">
      <div className="canvaschool-title-row">
        <div>
          <h1>학교 밖 갤러리</h1>
          <p className="canvaschool-page-lead">
            다른 학교 친구들이 자랑한 그림이에요. 반응을 남겨 보세요.
          </p>
        </div>
      </div>

      <div className="canvaschool-gallery-filters">
        {school ? (
          <div className="canvaschool-school-picked">
            <p role="status">선택한 학교: {school.name}</p>
            <button
              type="button"
              className="canvaschool-btn-secondary"
              onClick={() => {
                setSchool(null);
                setClassId(null);
              }}
            >
              모든 학교 보기
            </button>
          </div>
        ) : (
          <div className="canvaschool-form-group">
            <label
              htmlFor="canvaschool-gallery-school"
              className="canvaschool-form-label"
            >
              학교 찾기
            </label>
            <GallerySchoolPicker
              id="canvaschool-gallery-school"
              onChange={(picked) => {
                setSchool(picked);
                setClassId(null);
              }}
            />
          </div>
        )}
        {school && (
          <div
            className="canvaschool-gallery-chips"
            role="group"
            aria-label="학급 고르기"
          >
            <button
              type="button"
              className="canvaschool-gallery-chip"
              aria-pressed={classId === null}
              onClick={() => setClassId(null)}
            >
              전체
            </button>
            {classes.map((item) => (
              <button
                key={item.classId}
                type="button"
                className="canvaschool-gallery-chip"
                aria-pressed={classId === item.classId}
                onClick={() => setClassId(item.classId)}
              >
                {item.className} ({item.postCount})
              </button>
            ))}
          </div>
        )}
      </div>

      {!filtered && (
        <div className="canvaschool-tabs" role="tablist" aria-label="정렬">
          {SORTS.map((item) => (
            <button
              key={item.sort}
              type="button"
              role="tab"
              className="canvaschool-tab"
              aria-selected={sort === item.sort}
              onClick={() => setSort(item.sort)}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}

      {status === "loading" && <p aria-busy="true">불러오는 중…</p>}
      {status === "error" && error && (
        <>
          <ErrorAlert code={error} />
          <button type="button" onClick={() => setRetry((count) => count + 1)}>
            다시 시도
          </button>
        </>
      )}
      {status === "ready" && items.length === 0 && (
        <p className="canvaschool-hint">
          {filtered
            ? "이 조건에 맞는 작품이 아직 없어요."
            : "아직 올라온 작품이 없어요. 첫 작품의 주인공이 되어 보세요!"}
        </p>
      )}
      {items.length > 0 && (
        <ul className="canvaschool-gallery-grid" aria-label="작품 목록">
          {items.map((post) => (
            <GalleryCard
              key={post.id}
              post={post}
              onOpen={() => setOpenId(post.id)}
              onReaction={(state) => applyReaction(post.id, state)}
              onError={setError}
            />
          ))}
        </ul>
      )}
      {status === "ready" && error && <ErrorAlert code={error} />}
      {moreBusy && <p aria-busy="true">더 불러오는 중…</p>}
      {moreError && <ErrorAlert code={moreError} />}
      {status === "ready" && nextCursor && (
        <div ref={sentinelRef} className="canvaschool-gallery-sentinel">
          {(!canObserve || moreError) && (
            <button
              type="button"
              disabled={moreBusy}
              onClick={() => void loadMore()}
            >
              더 보기
            </button>
          )}
        </div>
      )}

      {openPost && (
        <GalleryViewer
          post={openPost}
          onClose={() => setOpenId(null)}
          onReaction={(state) => applyReaction(openPost.id, state)}
          onReported={() => patch(openPost.id, { reportedByMe: true })}
        />
      )}
    </section>
  );
};
